import {
  Breakpoints,
  styled,
  Text,
  TextAlignment,
  TextTag,
  TokenTextAppearance,
  TokenTextColor
} from '@volkswagen-onehub/components-core';
import React, {useContext, useEffect, useState} from 'react';
import {Dimension, ResponseVehicle} from '../../../types/api';
import {LengthUnitContext} from '../../../context/modules/dimensions/LengthUnitContext';
import {useFeatureServices} from '../../../use-feature-services';
import VisualToggle from '../Visuals/VisualToggle';
import useTrans from '../../../hooks/use-trans';
import { formatNumber} from '../../../utils/app.utils';
import { TrackingEvents } from '../../../types/enums/tracking-events.enum';
import { VisualContext } from '../../../context/modules/dimensions/VisualContext';
import { VisualToggleContext } from '../../../context/modules/dimensions/VisualToggleContext';
import { FAContent } from '../../../types/content';
import { Language } from '@vw-marketing/vw-ca-types';
import CompetitiveLengthWrapper from './wrappers/CompetitiveLengthWrapper';
import { addNbsp } from '../../../utils/addNbsp';

const DimensionsContainer = styled.div`
  display: grid;
`;

const StyledWrapper = styled.div<{hidden: boolean; isFrontmost: boolean}>`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  row-gap: ${(props) => props.theme.size.dynamic0020};
  text-align: center;
  border-radius: ${({theme}) => theme.size.static150};
  padding: ${(props) => props.theme.size.dynamic0040} 0;

  /* Default colors */
  color: ${({theme}) => theme.colors.content.active};
  background: ${({theme}) => theme.colors.background.tertiary};

  /* Box-shadow border based on state */
  ${({hidden, isFrontmost, theme}) => {
    if (hidden) {
      return `
        /* Disabled state - hidden from canvas */
        box-shadow: 0 0 0 ${theme.size.border.active} ${theme.colors.border.disabled};
        color: ${theme.colors.content.disabled};
      `;
    } else if (isFrontmost) {
      return `
        /* Frontmost vehicle - matches visualCtx */
        box-shadow: 0 0 0 ${theme.size.border.focus} ${theme.colors.border.hover};
      `;
    } else {
      return `
        /* Default selected state */
        box-shadow: 0 0 0 ${theme.size.border.active} ${theme.colors.border.hover};
      `;
    }
  }}

  span {
    text-decoration: none;
  }

  &:focus-within {
    &:has(#bring-to-front-button:focus-visible) {
      box-shadow: 0 0 0 ${({theme}) => theme.size.border.focus}
        ${({theme}) => theme.colors.focus.main};
      background: ${({theme}) => theme.colors.background.tertiary};
    }
    #jelly-toggle {
      opacity: 1;
    }
  }

  &:hover {
    transition: box-shadow 300ms ease, background 300ms ease;
    -webkit-transition: box-shadow 300ms ease, background 300ms ease;
    background: ${({theme}) => theme.colors.background.secondary};
    box-shadow: 0 0 0 ${({hidden, isFrontmost, theme}) => {
      if (hidden) {
        return theme.size.border.active;
      } else if (isFrontmost) {
        return theme.size.border.focus;
      } else {
        return theme.size.border.active;
      }
    }} ${({theme}) => theme.colors.border.hover};
    color: ${({theme}) => theme.colors.content.active};

    #jelly-toggle {
      opacity: 1;
    }
    &:focus-within:has(#bring-to-front-button:focus-visible) {
      box-shadow: 0 0 0 ${({theme}) => theme.size.border.focus} ${({theme}) => theme.colors.focus.main};
    }
  }
`;

const StyledTextContainer = styled(Text)`
  height: 100%;
`;

const StyledText = styled(Text)`
  align-self: start;
  display: flex;
  flex-direction: column;
`;

const UnitDetailsContainer = styled.div`
  margin: ${(props) => props.theme.size.dynamic0100} 0;
  width: 100%;
  height: 100%;
  padding: 0 8px;
  display: grid;
  grid-template-rows: 1fr auto;
  row-gap: 8px;
  align-items: end;
  justify-content: center;
  pointer-events: none;
  color: inherit;

  @media screen and (min-width: ${Breakpoints.b560}px) {
    margin: ${(props) => props.theme.size.dynamic0020} 0;
  }

`;

const UnitDetailsText = styled(Text)`
  line-height: 1.2;

  @media screen and (min-width: 1025px) {
    line-height: 1.5;
  }
`;

const CustomButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

const Length = ({
  vehicle,
}: {
  vehicle: ResponseVehicle;
}) => {
  const {
    'locale-service': localService,
    'fa-content-service': faContentService,
    tracking
  } = useFeatureServices();

  const language = localService.language;
  const [dimension, setDimension] = useState<Dimension | null>(null);
  const lengthUnitCtx = useContext(LengthUnitContext);
  const visualCtx = useContext(VisualContext);
  const visualToggleCtx = useContext(VisualToggleContext);
  const appContext = (faContentService?.content as FAContent)?.context;
  const showCustomNames = (faContentService?.content as FAContent)?.customNames;
  const vehicleContent = (faContentService?.content as FAContent)?.vehicles?.filter(x => x.acode === vehicle.acode)[0];

  const {
    sections: { dimensions },
    aria
  } = useTrans();

  const onTrackClick = () => {
    const exterior = vehicle?.exterior_colors?.[0];

    tracking.track(TrackingEvents.VEHICLEBUTTON_CLICK, {
      eventInfo: {
        eventAction: TrackingEvents.VEHICLEBUTTON_CLICK,
        contentId: "bring vehicle forward"
      },
      environment: {
        featureAppId: 'CompareDimensionsWidget-D6-CA',
        featureAppIdAppVersion: '1.0',
      },
      personalization: {
        status: false,
      },
      configuration: {
        CarlineId: vehicle?.carline_id,
        CarlineName: vehicle.model,
        ModelId: vehicle?.sales_code,
        ModelName: vehicle.trim,
        ModelYear: vehicle.year.toString(),
        ExteriorColorCode: exterior ? exterior?.hex_code : null,
        ExteriorColorName: exterior ? exterior?.label : null,
        PriceTotalTotal: vehicle.msrp
      },
      link: {
        name: vehicle.model + ' - brought forward - button',
        url: window.location.href
      }
    });
  };

  const handleClick = () => {

    onTrackClick();

    if (visualToggleCtx?.acodes.includes(vehicle.acode)) {
      visualToggleCtx?.set(vehicle.acode);
    }
    visualCtx?.set(vehicle);
  };

  useEffect(() => {
    const {dimensions} = vehicle;
    const unit = lengthUnitCtx?.unit!;
    if (dimensions) {
      setDimension(dimensions[unit]);
    }
  }, [lengthUnitCtx?.unit]);

  const getLabel = () => {
    if (showCustomNames === "true" && !!vehicleContent?.modelName?.trim().length) {
      return vehicleContent?.modelName;
    } else if (appContext === "suvw") {
      return vehicle.model;
    } else {
      return (
        <>
          {
            language === Language.FR as string
            ? `${vehicle.model} ${vehicle.year}`
            : `${vehicle.year} ${vehicle.model}`
          }
          <Text tag={TextTag.span} appearance={TokenTextAppearance.copy200} bold>
            {addNbsp(vehicle.trim)}
          </Text>
        </>
      );
    }
  }

  return dimension ? (
    <StyledTextContainer tag={TextTag.div} appearance={TokenTextAppearance.copy100} textAlign={TextAlignment.center}>
      <StyledWrapper
        hidden={visualToggleCtx?.acodes.includes(vehicle.acode) as boolean}
        isFrontmost={visualCtx?.vehicle?.acode === vehicle.acode}
      >
        <UnitDetailsContainer>
          <StyledText tag={TextTag.span} appearance={TokenTextAppearance.bigcopy100} color={TokenTextColor.inherit} bold>
            { getLabel() }
          </StyledText>
          <DimensionsContainer>
            <Text tag={TextTag.span} color={TokenTextColor.inherit}>
              {dimensions.title} ({dimensions.toggle[lengthUnitCtx?.unit as string]})
            </Text>
            <UnitDetailsText tag={TextTag.span} color={TokenTextColor.inherit} bold>
              {formatNumber(Number(dimension?.length), language as Language)}{' '}
              {dimensions.lengths.length}
              {formatNumber(Number(dimension?.width), language as Language)}{' '}
              {dimensions.lengths.width}
              {formatNumber(Number(dimension?.height), language as Language)}{' '}
              {dimensions.lengths.height}
            </UnitDetailsText>
            <Text tag={TextTag.span} color={TokenTextColor.inherit}>
              {dimensions.weight}
            </Text>
            <UnitDetailsText tag={TextTag.span} color={TokenTextColor.inherit} bold>
              {formatNumber(Number(vehicle.features?.weight), language as Language)}
            </UnitDetailsText>
          </DimensionsContainer>
        </UnitDetailsContainer>

        <CustomButton
          id="bring-to-front-button"
          aria-label={aria.dimensions.button}
          onClick={handleClick}
        />

        <VisualToggle vehicle={vehicle} />
      </StyledWrapper>
    </StyledTextContainer>
  ) : (
    <CompetitiveLengthWrapper />
  );
};

export default Length;
